// src/oura/components/StartPlanWizard.tsx
//
// Start a plan (ourteam-frontend#3712, `P3-2`).
//
// There was no way to start a plan in the product. `seedEngagement` existed and
// was called by nothing; `POST /businesses/{id}/engagement` was reachable only
// by hand. A signed contract became a live merchant through somebody running
// curl, which is why three merchants started paid work with their plan, their
// workspace and their login created in three separate steps that could each
// half-fail.
//
// Three steps: import the quotation's engagement.json and show what was parsed,
// check the category is one we sell to, then activate. Activation is ONE
// request (ourteam-backend#4809) — the screen never issues the seed, the
// binding and the login separately, because there is no DELETE route to undo
// the first if the second fails.

import { useState } from "react";
import { AlertTriangleIcon, CheckCircle2Icon, FileJsonIcon, Loader2Icon, UploadIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@component/dialog";
import { Button } from "../primitives/Button";
import { Chip } from "../primitives/Chip";
import { cn } from "../lib/cn";
import { parseEngagement, type ParsedEngagement } from "../lib/engagement";
import type { AeoScope } from "@service/aeo/types";
import {
    activateBusiness,
    isActivationFailure,
    type ActivationResult,
} from "@service/aeo/clientReview";

/** What step one builds: the seed, read by the shared quote reader. */
type Parsed = ParsedEngagement;

/**
 * The verdict on whether we sell to this category.
 *
 * Read from the server (ourteam-backend#4910), never recomputed here. The rule
 * is a keyword table in `dependencies/aeo_scope.py`, and a second copy of a
 * backend map is the failure this epic already fixed once for `covers[]`: it
 * drifts, and then the screen refuses a business the backend would accept.
 *
 * Null stays meaningful: the block is written at scan CREATION, so a business
 * nobody has scanned has no verdict. "Not scored" and "low priority" are
 * different facts and step two says so.
 *
 * IN SCOPE IS TIER 1 OR 2. Tier 3 is the catalogue's "this category rarely
 * needs AEO", tier 0 is "detect returned nothing to score against" — neither is
 * a yes, and both are overridable with a written reason rather than refused.
 */
export interface ScopeVerdict {
    tier: number;
    label: string;
    inScope: boolean;
}

/** `AeoScope` off the list row, as this step reads it. */
export function scopeVerdictOf(scope: AeoScope | null | undefined): ScopeVerdict | null {
    if (!scope || typeof scope.priority_tier !== "number") return null;
    return {
        tier: scope.priority_tier,
        label: scope.scope_warning || scope.priority_label,
        inScope: scope.priority_tier === 1 || scope.priority_tier === 2,
    };
}

const STEPS = ["Import", "Scope", "Activate"] as const;

export function StartPlanWizard({
    open,
    businessId,
    businessName,
    category,
    scope = null,
    onOpenChange,
    onActivated,
}: {
    open: boolean;
    businessId: string;
    businessName: string;
    category?: string;
    scope?: ScopeVerdict | null;
    onOpenChange: (v: boolean) => void;
    onActivated?: (r: ActivationResult) => void;
}) {
    const [step, setStep] = useState(0);
    const [text, setText] = useState("");
    const [parsed, setParsed] = useState<Parsed | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const [override, setOverride] = useState("");
    const [contact, setContact] = useState({ name: "", email: "" });
    const [busy, setBusy] = useState(false);
    const [failure, setFailure] = useState<{ code: string | null; message: string } | null>(null);
    const [result, setResult] = useState<ActivationResult | null>(null);

    const reset = () => {
        setStep(0); setText(""); setParsed(null); setParseError(null);
        setOverride(""); setContact({ name: "", email: "" });
        setBusy(false); setFailure(null); setResult(null);
    };

    const readFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => { setText(String(reader.result ?? "")); setParseError(null); };
        reader.readAsText(file);
    };

    const doParse = () => {
        try {
            setParsed(parseEngagement(text));
            setParseError(null);
            setStep(1);
        } catch (err) {
            setParsed(null);
            setParseError(err instanceof Error ? err.message : "The file could not be read.");
        }
    };

    // Blocked on anything other than a confirmed in-scope verdict, UNLESS a
    // staffer writes down why. An unscored category is not a refusal — the
    // reason is simply missing — but it is not a silent yes either.
    const scopeBlocks = !scope?.inScope;
    const mayLeaveScope = !scopeBlocks || override.trim().length > 0;

    const activate = async () => {
        if (!parsed) return;
        setBusy(true);
        setFailure(null);
        const r = await activateBusiness(businessId, {
            engagement: parsed.seed,
            contact: { name: contact.name.trim(), email: contact.email.trim() },
        });
        setBusy(false);
        if (isActivationFailure(r)) { setFailure(r); return; }
        setResult(r);
        onActivated?.(r);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Start a plan</DialogTitle>
                    <DialogDescription>
                        {businessName} · the quotation becomes a live plan, a workspace and a merchant login.
                    </DialogDescription>
                </DialogHeader>

                {!result && (
                    <div className="mb-4 flex items-center gap-2">
                        {STEPS.map((label, i) => (
                            <div key={label} className="flex items-center gap-2">
                                <span className={cn(
                                    "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                                    i === step ? "bg-blue-600 text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400",
                                )}>{i + 1}</span>
                                <span className={cn("text-sm", i === step ? "font-medium text-gray-900" : "text-gray-400")}>{label}</span>
                                {i < STEPS.length - 1 && <span className="mx-1 h-px w-6 bg-gray-200" />}
                            </div>
                        ))}
                    </div>
                )}

                {/* ---------------------------------------------------------- 1 */}
                {!result && step === 0 && (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-500">
                            The <code className="rounded bg-gray-100 px-1 text-xs">engagement.json</code> the quotation build wrote, beside the PDF.
                        </p>
                        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-600 hover:border-blue-400 hover:bg-blue-50/40">
                            <UploadIcon className="h-4 w-4 text-gray-400" />
                            Choose engagement.json
                            <input
                                type="file"
                                accept=".json,application/json"
                                className="hidden"
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) readFile(f); }}
                            />
                        </label>
                        <textarea
                            value={text}
                            onChange={(e) => { setText(e.target.value); setParseError(null); }}
                            rows={7}
                            placeholder="…or paste the file's contents here"
                            className="w-full rounded-md border border-gray-300 p-2 font-mono text-xs text-gray-800 focus:border-blue-600 focus:outline-none"
                        />
                        {parseError && (
                            <p className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />{parseError}
                            </p>
                        )}
                        <div className="flex justify-end">
                            <Button disabled={!text.trim()} onClick={doParse}><FileJsonIcon />Read the file</Button>
                        </div>
                    </div>
                )}

                {/* ---------------------------------------------------------- 2 */}
                {!result && step === 1 && parsed && (
                    <div className="space-y-4">
                        <ParsedSummary parsed={parsed} />
                        <div className={cn(
                            "rounded-lg border px-3 py-2.5 text-sm",
                            scope?.inScope ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                : scope ? "border-amber-200 bg-amber-50 text-amber-800"
                                    : "border-gray-200 bg-gray-50 text-gray-700",
                        )}>
                            <p className="font-medium">
                                {scope?.inScope ? "In scope" : scope ? "Out of the categories we sell to" : "Not scored"}
                                {category ? <span className="font-normal"> · {category}</span> : null}
                            </p>
                            <p className="mt-0.5">
                                {scope?.label ?? "No scope verdict reaches the frontend yet, so this has not been checked against the category rules. Say why this business is worth activating and it is recorded with the plan."}
                            </p>
                        </div>
                        {scopeBlocks && (
                            <textarea
                                value={override}
                                onChange={(e) => setOverride(e.target.value)}
                                rows={2}
                                placeholder="Why this one is worth activating anyway. Required."
                                className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none"
                            />
                        )}
                        <div className="flex justify-between">
                            <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
                            <Button disabled={!mayLeaveScope} onClick={() => setStep(2)}>Continue</Button>
                        </div>
                    </div>
                )}

                {/* ---------------------------------------------------------- 3 */}
                {!result && step === 2 && parsed && (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-500">
                            Who signs in as the merchant. They get an invitation to set their own password.
                        </p>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <input
                                value={contact.name}
                                onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                                placeholder="Their name"
                                className="h-9 rounded-md border border-gray-300 px-2 text-sm focus:border-blue-600 focus:outline-none"
                            />
                            <input
                                value={contact.email}
                                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                                placeholder="their@email.com"
                                className="h-9 rounded-md border border-gray-300 px-2 text-sm focus:border-blue-600 focus:outline-none"
                            />
                        </div>
                        {failure && (
                            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
                                <p className="flex items-start gap-2">
                                    <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                    {/* The server's own sentence. It is the only thing that
                                        knows whether anything was written. */}
                                    <span>{failure.message}</span>
                                </p>
                                {failure.code && <p className="mt-1 pl-6 text-xs text-rose-600">{failure.code}</p>}
                            </div>
                        )}
                        <div className="flex justify-between">
                            <Button variant="outline" disabled={busy} onClick={() => { setFailure(null); setStep(1); }}>Back</Button>
                            <Button disabled={busy || !contact.name.trim() || !contact.email.includes("@")} onClick={() => void activate()}>
                                {busy ? <Loader2Icon className="animate-spin" /> : <CheckCircle2Icon />}
                                {busy ? "Activating" : "Activate"}
                            </Button>
                        </div>
                    </div>
                )}

                {/* ------------------------------------------------------- done */}
                {result && (
                    <div className="space-y-3">
                        <p className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
                            <CheckCircle2Icon className="h-4 w-4 flex-shrink-0" />
                            {result.engagement.ref} is live. The plan, the workspace and the login all landed together.
                        </p>
                        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                            <dt className="text-gray-500">Merchant signs in as</dt>
                            <dd className="font-medium text-gray-900">{result.merchant.email}</dd>
                            <dt className="text-gray-500">Their workspace</dt>
                            <dd className="truncate text-gray-900">{result.merchant.workspaceUrl}</dd>
                            <dt className="text-gray-500">Plan</dt>
                            <dd className="text-gray-900">
                                {result.engagement.planName || "—"} · {result.engagement.deliverables.length} lines over {result.engagement.termMonths} months
                            </dd>
                        </dl>
                        <div className="flex justify-end">
                            <Button onClick={() => { reset(); onOpenChange(false); }}>Open the plan</Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

/** Everything the file said, so a staffer checks it against the PDF before it becomes a contract. */
function ParsedSummary({ parsed }: { parsed: Parsed }) {
    const { seed, unknownKeys } = parsed;
    return (
        <div className="space-y-3">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-gray-500">Ref</dt><dd className="font-medium text-gray-900">{seed.ref}</dd>
                <dt className="text-gray-500">Client</dt><dd className="text-gray-900">{seed.client || "—"}</dd>
                <dt className="text-gray-500">Plan</dt><dd className="text-gray-900">{seed.planName || "—"}</dd>
                <dt className="text-gray-500">Term</dt><dd className="text-gray-900 tabular-nums">{seed.termMonths} months</dd>
                <dt className="text-gray-500">Pages</dt><dd className="text-gray-900 tabular-nums">{seed.pageUnits}</dd>
                <dt className="text-gray-500">Outlets</dt><dd className="text-gray-900 tabular-nums">{seed.outlets}</dd>
                <dt className="text-gray-500">Starts</dt>
                <dd className="text-gray-900">{seed.startedAt ?? "When payment clears"}</dd>
            </dl>

            {unknownKeys.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                    <p className="font-medium">
                        {unknownKeys.length} key{unknownKeys.length === 1 ? "" : "s"} in this file {unknownKeys.length === 1 ? "is" : "are"} not part of an engagement
                    </p>
                    <p className="mt-0.5 flex flex-wrap gap-1.5">
                        {unknownKeys.map((k) => <Chip key={k} className="border-amber-200 bg-amber-100/60 text-amber-800">{k}</Chip>)}
                    </p>
                    <p className="mt-1.5 text-xs">
                        They are not sent and nothing will carry them. If one of them is a term we agreed, it does not exist in the product yet.
                    </p>
                </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full min-w-max lg:min-w-0">
                    <thead><tr className="border-b border-gray-200 bg-gray-50">
                        <th className="w-14 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Code</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">What the merchant sees</th>
                        <th className="w-32 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Months</th>
                        <th className="w-40 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Quantity</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                        {seed.deliverables.map((d, i) => (
                            <tr key={`${d.code}-${i}`}>
                                <td className="px-3 py-1.5 text-xs font-semibold text-gray-400 tabular-nums">{d.code || "—"}</td>
                                <td className="px-3 py-1.5 text-sm text-gray-900">{d.label || <span className="text-gray-400">—</span>}</td>
                                <td className="px-3 py-1.5 text-sm text-gray-700 tabular-nums">{d.months.join(", ") || "—"}</td>
                                <td className="px-3 py-1.5 text-sm text-gray-700">{d.quantity ?? "—"}</td>
                            </tr>
                        ))}
                        {seed.deliverables.length === 0 && (
                            <tr><td colSpan={4} className="px-3 py-4 text-center text-sm text-gray-400">No deliverable lines in this file.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {seed.deviations.length > 0 && (
                <p className="text-xs text-gray-500">
                    {/* Staff-only by P0-5 — never rendered on a merchant surface. */}
                    <span className="font-medium text-gray-700">From the catalogue:</span> {seed.deviations.join(" · ")}
                </p>
            )}
        </div>
    );
}
